import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, BadgeCheck } from "lucide-react";
import { API } from "@/App";
import { resolveAssetUrl } from "@/lib/assetUrl";

// ── Design tokens ────────────────────────────────────────────────────────
// Rail is intentionally horizontally-scrollable — one gesture on mobile,
// wheel/touchpad on desktop. We render at most `limit` cards regardless of
// window because a rail is meant to feel curated, not exhaustive.

// Turn an ISO date into "5m ago", "3h ago", "2d ago". Falls back to a
// human date string once the drop is older than a week so the rail never
// shouts "247h ago" at anyone.
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Small avatar circle for the business that posted the drop.
function BizAvatar({ business }) {
  const src = business?.logo_url ? resolveAssetUrl(business.logo_url) : null;
  const initials = (business?.name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
  return (
    <div className="flex-none h-7 w-7 rounded-full bg-tbc-navy/80 ring-2 ring-tbc-navy overflow-hidden flex items-center justify-center">
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-[10px] font-heading text-tbc-gold">{initials}</span>
      )}
    </div>
  );
}

/**
 * Latest Drops hero rail.
 *
 * @param {"blinterest"|"landing"} placement - controls headline copy +
 *   background. Blinterest uses a navy-in-navy variant; the landing page
 *   uses a stand-alone cream/navy card so it reads as a distinct feature.
 * @param {number|null} windowHours - when set, hits the backend with a
 *   recency window (e.g. 168 for "this week"). Null returns the newest
 *   `limit` drops regardless of age.
 * @param {number} limit - number of cards to fetch.
 * @param {string} [headline] - optional override for the section heading
 *   (e.g. "Fresh Drops This Week"). Falls back to a sensible default per
 *   placement when omitted.
 * @param {string} [kicker] - optional override for the sub-heading line
 *   under the headline.
 * @param {"dark"|"cream"} [variant] - visual treatment. `dark` fits inside
 *   a navy band (Blinterest, or a homepage navy band). `cream` renders as
 *   a rounded editorial card on the light homepage background.
 */
export default function LatestDropsRail({
  placement = "blinterest",
  windowHours = null,
  limit = 12,
  headline,
  kicker,
  variant,
}) {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ limit: String(limit) });
        if (windowHours) params.set("window_hours", String(windowHours));
        const r = await axios.get(`${API}/blinterest/latest-drops?${params}`);
        if (!cancelled) setDrops(r.data?.drops || []);
      } catch {
        if (!cancelled) setDrops([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [windowHours, limit]);

  // Nothing to show — bail cleanly so the page doesn't render an empty
  // rail scaffold. Loading state also renders null so we don't flash
  // skeletons; the fetch is <200ms.
  if (loading || drops.length === 0) return null;

  // Effective visual mode: caller can override, otherwise pick a sensible
  // default from placement so we don't break the two existing insertion
  // sites when adding new ones later.
  const useDark = variant ? variant === "dark" : placement === "blinterest";
  const defaultHeadline = placement === "blinterest" ? "Latest Drops" : "Fresh Drops This Week";
  const defaultKicker = placement === "blinterest"
    ? "Freshly posted by the community"
    : "New this week from Black-owned businesses";
  const resolvedHeadline = headline || defaultHeadline;
  const resolvedKicker = kicker || defaultKicker;

  return (
    <section
      className={
        useDark
          ? "px-4 sm:px-6 lg:px-10 pt-4 pb-6"
          : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14"
      }
      data-testid={`latest-drops-rail-${placement}`}
    >
      <div className={useDark ? "" : "rounded-3xl bg-tbc-cream/80 p-5 sm:p-8 lg:p-10"}>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4 sm:mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 uppercase text-[10px] sm:text-xs tracking-widest font-semibold text-tbc-gold">
              <Sparkles className="h-3.5 w-3.5" />
              Live
            </div>
            <h2
              className={`font-heading leading-tight mt-1 ${useDark ? "text-white" : "text-tbc-navy"}`}
              style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)" }}
            >
              {resolvedHeadline}
            </h2>
            <p className={`mt-1 font-body text-sm ${useDark ? "text-white/70" : "text-gray-600"}`}>
              {resolvedKicker}
            </p>
          </div>
          <Link
            to="/blinterest"
            className={`text-sm font-body font-medium ${useDark ? "text-tbc-gold hover:text-tbc-gold/80" : "text-tbc-navy hover:text-tbc-gold"} transition-colors`}
            data-testid="latest-drops-view-all"
          >
            View all Drops →
          </Link>
        </div>

        {/* Horizontal scroll rail. `snap-x` gives satisfying paging on
            touch; `no-scrollbar` keeps the visual clean without hiding
            keyboard focus. */}
        <div
          className="-mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10 flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2"
          style={{ scrollbarWidth: "thin" }}
          data-testid="latest-drops-scroller"
        >
          {drops.map((drop) => {
            const img = resolveAssetUrl(drop.image_url);
            const biz = drop.business;
            return (
              <Link
                key={drop.pin_id}
                to={`/blinterest/pin/${drop.pin_id}`}
                className="flex-none w-40 sm:w-48 rounded-2xl overflow-hidden bg-tbc-navy ring-1 ring-white/10 hover:ring-tbc-gold/60 hover:-translate-y-0.5 transition-all snap-start"
                data-testid={`latest-drops-card-${drop.pin_id}`}
              >
                {/* Image (aspect-square keeps the rail visually rhythmic) */}
                <div className="relative aspect-square bg-tbc-navy overflow-hidden">
                  {img ? (
                    <img
                      src={img}
                      alt={drop.title || "Drop"}
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-tbc-gold/50">
                      <Sparkles className="h-8 w-8" />
                    </div>
                  )}
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-tbc-gold px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-tbc-navy">
                    Fresh
                  </span>
                  <span className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                    {timeAgo(drop.created_at)}
                  </span>
                </div>
                {/* Meta strip */}
                <div className="p-2.5 sm:p-3">
                  <p
                    className="font-body text-xs sm:text-sm text-white leading-snug line-clamp-2 min-h-[2.4em]"
                    title={drop.title}
                  >
                    {drop.title || "Untitled drop"}
                  </p>
                  {biz && (
                    <div className="mt-2 flex items-center gap-2">
                      <BizAvatar business={biz} />
                      <div className="min-w-0">
                        <p className="text-[11px] font-heading text-tbc-gold truncate flex items-center gap-1">
                          {biz.name}
                          {biz.is_verified && (
                            <BadgeCheck className="h-3 w-3 flex-none" />
                          )}
                        </p>
                        {(biz.city || biz.state) && (
                          <p className="text-[10px] text-white/50 truncate">
                            {[biz.city, biz.state].filter(Boolean).join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
